import { c as _c } from "react/compiler-runtime";
import { IcoClose16 } from '@onefootprint/icons';
import type { OffsetPaginatedPlaybookVersionNoGraph, Playbook, PlaybookSettings, PlaybookVersionNoGraph } from '@onefootprint/request-types/dashboard';
import { IconButton } from '@onefootprint/ui';
import type { QueryObserverResult } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PlaybookBuilder, { useStore } from '@/components/playbook-builder';
import PlaybookGraphHeader from '@/features/playbook-graph-header';
import PlaybookVersionBadge from '@/features/playbook-version-badge';
import PlaybookVersionsDropdown from '@/features/playbook-versions-dropdown';
import ActionsDropdown from './components/actions-dropdown/actions-dropdown';
import DeploymentDialog from './components/deployment-dialog/deployment-dialog';
import PublishActions from './components/publish-actions';
import TestMode from './components/test-mode';
type PlaybookEditProps = {
  playbook: Playbook;
  selectedVersionNoGraph: PlaybookVersionNoGraph;
  settings: PlaybookSettings;
  playbookVersions: PlaybookVersionNoGraph[];
  onRefetch: () => Promise<QueryObserverResult<OffsetPaginatedPlaybookVersionNoGraph, Error>>;
  onClose: () => void;
  canEdit: boolean;
};
const PlaybookEdit = t0 => {
  const $ = _c(59);
  const {
    playbook,
    selectedVersionNoGraph,
    settings,
    playbookVersions,
    onRefetch,
    onClose,
    canEdit
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "view"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const store = useStore();
  const [deploymentDialogMode, setDeploymentDialogMode] = useState(undefined);
  const [deployDefaultVersions, setDeployDefaultVersions] = useState(undefined);
  let t2;
  if ($[1] !== selectedVersionNoGraph) {
    t2 = versionId => {
      setDeployDefaultVersions([{
        versionId: versionId ?? selectedVersionNoGraph.id,
        weight: 100
      }]);
      setDeploymentDialogMode("create");
    };
    $[1] = selectedVersionNoGraph;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleDeploy = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setDeployDefaultVersions(undefined);
      setDeploymentDialogMode("view");
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleViewDeployment = t3;
  let t4;
  if ($[4] !== t) {
    t4 = t(_temp);
    $[4] = t;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== onClose || $[7] !== t4) {
    t5 = <IconButton aria-label={t4} onClick={onClose} size="compact" icon={IcoClose16} />;
    $[6] = onClose;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = settings?.name || playbook.publishedVersion.name;
  let t7;
  if ($[9] !== playbook.hasProdOnboarding || $[10] !== playbook.id || $[11] !== t6) {
    t7 = {
      hasProdOnboarding: playbook.hasProdOnboarding,
      id: playbook.id,
      name: t6
    };
    $[9] = playbook.hasProdOnboarding;
    $[10] = playbook.id;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== canEdit || $[14] !== selectedVersionNoGraph || $[15] !== t7) {
    t8 = <ActionsDropdown canEdit={canEdit} playbook={t7} publishedVersion={selectedVersionNoGraph} />;
    $[13] = canEdit;
    $[14] = selectedVersionNoGraph;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== store) {
    t9 = () => store.reset();
    $[17] = store;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== playbook || $[20] !== t9) {
    t10 = <PlaybookVersionsDropdown playbook={playbook} onChange={t9} />;
    $[19] = playbook;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== playbook.activeDeployment || $[23] !== selectedVersionNoGraph) {
    t11 = <PlaybookVersionBadge version={selectedVersionNoGraph} activeDeployment={playbook.activeDeployment} />;
    $[22] = playbook.activeDeployment;
    $[23] = selectedVersionNoGraph;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t10 || $[26] !== t11 || $[27] !== t5 || $[28] !== t8) {
    t12 = <div className="flex gap-2 items-center min-w-0 md:min-w-auto">{t5}{t8}{t10}{t11}</div>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t5;
    $[28] = t8;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== handleDeploy || $[31] !== onRefetch || $[32] !== playbook || $[33] !== playbookVersions || $[34] !== selectedVersionNoGraph) {
    t13 = <PublishActions playbook={playbook} playbookVersion={selectedVersionNoGraph} playbookVersions={playbookVersions} onRefetch={onRefetch} onDeploy={handleDeploy} onViewDeployment={handleViewDeployment} />;
    $[30] = handleDeploy;
    $[31] = onRefetch;
    $[32] = playbook;
    $[33] = playbookVersions;
    $[34] = selectedVersionNoGraph;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  const t14 = !!store.data.history.hasChanges;
  let t15;
  if ($[36] !== store.visibleBanners) {
    t15 = store.visibleBanners.some(_temp2);
    $[36] = store.visibleBanners;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== selectedVersionNoGraph || $[39] !== store.data.obcId || $[40] !== t14 || $[41] !== t15) {
    t16 = <TestMode selectedVersion={selectedVersionNoGraph} hasPendingChanges={t14} hasErrors={t15} versionId={store.data.obcId} />;
    $[38] = selectedVersionNoGraph;
    $[39] = store.data.obcId;
    $[40] = t14;
    $[41] = t15;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== t13 || $[44] !== t16) {
    t17 = <>{t13}{t16}</>;
    $[43] = t13;
    $[44] = t16;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== t12 || $[47] !== t17) {
    t18 = <PlaybookBuilder className="flex-1 min-h-0" headerComponent={<PlaybookGraphHeader slots={{
      left: t12,
      right: t17
    }} />} />;
    $[46] = t12;
    $[47] = t17;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  const t19 = deploymentDialogMode !== undefined;
  let t20;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => setDeploymentDialogMode(undefined);
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  const t21 = deploymentDialogMode ?? "view";
  let t22;
  if ($[50] !== deployDefaultVersions || $[51] !== playbook || $[52] !== playbookVersions || $[53] !== t19 || $[54] !== t21) {
    t22 = <DeploymentDialog open={t19} onClose={t20} playbook={playbook} playbookVersions={playbookVersions} defaultMode={t21} defaultVersions={deployDefaultVersions} />;
    $[50] = deployDefaultVersions;
    $[51] = playbook;
    $[52] = playbookVersions;
    $[53] = t19;
    $[54] = t21;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  let t23;
  if ($[56] !== t18 || $[57] !== t22) {
    t23 = <>{t18}{t22}</>;
    $[56] = t18;
    $[57] = t22;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  return t23;
};
export default PlaybookEdit;
function _temp($) {
  return $.close;
}
function _temp2(b) {
  return b.type === "error";
}